import type { RunAggregateProjectionV2, WebTestRun } from '@blume/web-test-model'
import { useMemo } from 'react'

import { shortSha } from '@/design/format'
import { toneText } from '@/design/status'
import { cn } from '@/lib/utils'
import { fallbackSurface, PullRequestAuthorAvatar } from '@/ui/atoms/avatar-tile'

import { EmptyState, GroupCard, MetaLine, PageTitle, RunList } from './parts'
import { PullRequestGitHubFacts } from './pr-metadata'
import { mergeAnswer, pullRequestGroups, pullRequestHref, stalePullRequestKeys } from './pr-shared'

export function PullRequestsPage({
  runs,
  aggregates,
  now
}: {
  runs: WebTestRun[]
  aggregates?: RunAggregateProjectionV2[]
  now: number
}) {
  const groups = useMemo(() => pullRequestGroups(runs, aggregates), [aggregates, runs])
  const stale = useMemo(() => stalePullRequestKeys(groups), [groups])

  return (
    <>
      <PageTitle
        title="Pull requests"
        subtitle="Verification grouped by pull request revision — is it safe to merge? Open a pull request for the full picture."
      />
      {groups.length === 0 ? (
        <EmptyState>No pull-request runs yet.</EmptyState>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => {
            const progress = group.progress
            const answer = mergeAnswer(progress)
            const AnswerIcon = answer.icon
            const superseded = stale.has(group.key)
            return (
              <section
                key={group.key}
                aria-label={group.label}
                className={cn(superseded && 'opacity-60')}
              >
                <GroupCard
                  link={{
                    href: pullRequestHref(group.key),
                    label: `Open ${group.label}${
                      group.sourceSha ? ` revision ${shortSha(group.sourceSha)}` : ''
                    } — pull request page`
                  }}
                  header={
                    <>
                      <div className="flex items-start gap-3">
                        {/* The card itself is the link to the PR page; the
                         * title only carries the visual affordance for it. */}
                        <PullRequestAuthorAvatar
                          author={group.metadata?.author}
                          size="regular"
                          label={`Pull request ${group.pullRequest ?? ''}`}
                        />
                        <div className="min-w-0">
                          <h2 className="text-xl leading-tight font-semibold tracking-tight">
                            {group.metadata?.title ?? 'Pull request'}
                          </h2>
                          {group.pullRequest ? (
                            <p className="mt-0.5 text-[13px] leading-4 text-muted-foreground">
                              PR{' '}
                              <span
                                className="font-mono font-medium tabular-nums"
                                style={{
                                  color: fallbackSurface(`pull-request:${group.pullRequest}`)
                                    .backgroundColor
                                }}
                              >
                                #{group.pullRequest}
                              </span>
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-2">
                        <MetaLine
                          items={[
                            group.repository,
                            group.sourceSha ? (
                              <span key="sha" className="font-mono">
                                commit {shortSha(group.sourceSha)}
                              </span>
                            ) : undefined,
                            superseded ? 'Superseded by a newer revision' : undefined
                          ]}
                        />
                      </div>
                      <div
                        aria-label="Merge readiness"
                        className="mt-3 rounded-md border border-border/80 bg-card/45 px-3 py-2.5"
                      >
                        <p
                          className={cn(
                            'inline-flex items-center gap-2 text-[15px] font-medium',
                            toneText[answer.tone]
                          )}
                        >
                          <AnswerIcon
                            aria-hidden
                            className={cn('size-4', answer.tone === 'info' && 'animate-spin')}
                          />
                          {answer.label}
                        </p>
                        {group.metadata ? (
                          <div className="mt-2 border-t border-border/70 pt-2">
                            <PullRequestGitHubFacts metadata={group.metadata} />
                          </div>
                        ) : null}
                      </div>
                    </>
                  }
                >
                  {group.runs.length > 0 ? (
                    <RunList runs={group.runs} now={now} hideSource avatarSize="small" />
                  ) : progress.primaryRunId ? (
                    <p className="py-2 text-[13px] text-muted-foreground">
                      The decisive run is outside the loaded page.{' '}
                      <a
                        href={`#/runs/${encodeURIComponent(progress.primaryRunId)}`}
                        className="relative font-medium text-foreground underline underline-offset-4 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                      >
                        Open run
                      </a>
                    </p>
                  ) : null}
                </GroupCard>
              </section>
            )
          })}
        </div>
      )}
    </>
  )
}
